<script setup lang="ts">
import { computed } from 'vue';
import { DAY_IDS } from '@/utils/customPlaylistSchedule';

/**
 * The days a repeating schedule runs on. All seven collapse to the word
 * "Every day" (70% of repeating rows in production), so the chips only appear when
 * the days really differ. Sunday first, because `delimited_day_ids` is
 * Sunday = 1 … Saturday = 7.
 */
const props = defineProps<{ days: number[] | 'daily' }>();

const chips = computed(() =>
    props.days === 'daily'
        ? []
        : DAY_IDS.map((day) => ({ ...day, on: (props.days as number[]).includes(day.id) })),
);

const label = computed(() => {
    if (props.days === 'daily') return 'Every day';
    const names = chips.value.filter((chip) => chip.on).map((chip) => chip.name);
    return names.length > 0 ? `Runs on ${names.join(', ')}` : 'No days selected';
});
</script>

<template>
    <span v-if="days === 'daily'" data-testid="schedule-days">Every day</span>
    <span
        v-else
        class="inline-flex shrink-0 gap-0.5"
        role="img"
        :aria-label="label"
        :title="label"
        data-testid="schedule-days"
    >
        <i
            v-for="chip in chips"
            :key="chip.id"
            class="inline-block h-3.5 w-[13px] rounded-[2px] text-center text-[9px] leading-[14px] font-semibold not-italic"
            :class="chip.on ? 'bg-accent-border text-accent-foreground' : 'bg-muted-foreground/15 text-muted-foreground'"
            :data-on="chip.on ? 'true' : 'false'"
            aria-hidden="true"
            >{{ chip.short }}</i
        >
    </span>
</template>
